import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { formatApiError, errContext } from "../lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-fill email from query string so post-activation lands in the right seat.
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfa) {
        navigate("/mfa");
      } else {
        toast.success("Welcome back");
        // Superuser goes to platform console; everyone else lands on the tenant dashboard.
        navigate(res.user?.role === "superuser" ? "/platform" : "/");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, errContext(err)) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-screen grid grid-cols-1 lg:grid-cols-[1fr_480px] bg-white">
      {/* Hero panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-zinc-950 bg-zinc-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="relative">
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-2">SMARTERP / V1.0</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
            Enterprise<br />
            <span className="text-[#FF3E00]">precision.</span><br />
            At scale.
          </h1>
          <p className="text-sm text-zinc-600 max-w-md mt-6 leading-relaxed">
            An ERP built like a control instrument. Finance, HR, and Supply Chain — unified under one secure system with AI assistance and multi-factor authentication by default.
          </p>
        </div>

        <div className="relative space-y-2 border-t border-zinc-300 pt-6">
          {[
            "Finance / GL · AR · AP · IFRS 9·15·16",
            "Oman Compliance / VAT Return · Fawtara e-Invoice",
            "Banking H2H / MT940 · CAMT.053 · Auto-match",
            "AML Watchlist / OFAC · UN · PEP screening",
            "Consolidation / Multi-entity · FX · Elimination",
            "Human Resources / Payroll · Leaves · AI Approval Coach",
            "Supply Chain / Inventory · POs · SOs",
            "AI Copilot / Voice Journals · Anomaly Watch · Board Snapshot",
            "Security / TOTP · Email OTP · OIDC · SAML SSO",
          ].map((row, i) => (
            <div key={row} className="flex items-center justify-between text-xs">
              <span className="font-mono text-zinc-500">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex-1 px-3 text-zinc-700">{row}</span>
              <span className="w-1.5 h-1.5 bg-[#FF3E00]" />
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col justify-center px-8 sm:px-12 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-4">/ ACCESS_TERMINAL</div>
          <h2 className="font-display text-3xl font-black tracking-tight">Sign in.</h2>
          <p className="text-sm text-zinc-500 mt-1">Use your administrator credentials.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="label-sm text-zinc-500">Email</label>
              <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2 transition-colors">
                <EnvelopeSimple size={16} className="text-zinc-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-transparent outline-none text-sm"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="label-sm text-zinc-500">Password</label>
              <div className="flex items-center gap-3 border-b border-zinc-900/20 focus-within:border-zinc-950 py-2 transition-colors">
                <Lock size={16} className="text-zinc-400" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="flex-1 bg-transparent outline-none text-sm"
                />
              </div>
            </div>

            {error && (
              <div className="border border-[#D0021B] bg-red-50 text-[#D0021B] text-xs px-3 py-2 font-mono" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? "AUTHENTICATING…" : "SIGN IN"}
              {!loading && <ArrowRight size={14} weight="bold" />}
            </button>

            <div className="text-xs text-zinc-500 flex items-center justify-between">
              <Link to="/reset-password" data-testid="login-forgot-password-link" className="text-zinc-950 underline underline-offset-4 hover:text-[#FF3E00]">
                Forgot password?
              </Link>
              <Link to="/register" data-testid="link-register" className="text-zinc-950 underline underline-offset-4 hover:text-[#FF3E00]">
                Request access
              </Link>
            </div>
          </form>

          <Link to="/cash-health-check" data-testid="link-health-check" className="block mt-6 border border-zinc-300 hover:border-[#FF3E00] hover:bg-zinc-50 p-4 transition-all group">
            <div className="font-mono text-[9px] tracking-[0.2em] text-[#FF3E00]">FREE TOOL · NO SIGNUP</div>
            <div className="font-display font-bold text-base mt-1 group-hover:text-[#FF3E00] transition-colors">Cash Flow Health Check →</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Get an AI-powered CFO assessment in 30 seconds.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
